import { FC, memo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import type { FilterChip, FilterKey } from "../types";
import { FilterChipWithTooltip } from "./FilterChipWithTooltip";

export type FiltersChipsBarProps = {
  chips: FilterChip[];
  onClearAll?: () => void;
  onChipClick?: (filterKey: FilterKey) => void;
  hideWhenEmpty?: boolean;
  /**
   * When set, render at most this many chips and collapse the remainder into a
   * single "+N more" chip whose tooltip lists the hidden filters.
   */
  maxVisible?: number;
  /** Display-only: hide per-chip delete icons and the "Clear All" button. */
  readOnly?: boolean;
  /** Click handler for the "+N more" overflow chip. */
  onOverflowClick?: () => void;
  /** Overrides for the container Box (e.g. to reset margins when inline). */
  sx?: SxProps<Theme>;
};

const chipSx = {
  bgcolor: "primary.lighter",
  color: "primary.dark",
  cursor: "pointer",
  "& .MuiChip-deleteIcon": {
    color: "primary.dark",
    "&:hover": {
      color: "primary.dark",
    },
  },
};

const disabledChipSx = {
  bgcolor: "grey.100",
  color: "text.disabled",
  border: "1px dashed",
  borderColor: "grey.400",
  opacity: 0.7,
  cursor: "pointer",
  "& .MuiChip-deleteIcon": {
    color: "text.disabled",
    "&:hover": {
      color: "text.secondary",
    },
  },
};

export const FiltersChipsBar: FC<FiltersChipsBarProps> = memo(
  ({
    chips,
    onClearAll,
    onChipClick,
    hideWhenEmpty = true,
    maxVisible,
    readOnly = false,
    onOverflowClick,
    sx,
  }) => {
    if (hideWhenEmpty && chips.length === 0) return null;

    const visibleChips =
      maxVisible != null ? chips.slice(0, maxVisible) : chips;
    const hiddenChips = maxVisible != null ? chips.slice(maxVisible) : [];

    return (
      <Box
        sx={[
          {
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            mb: 1,
            ml: 1,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {visibleChips.map((chip) => (
          <FilterChipWithTooltip
            key={chip.id}
            chip={chip}
            onClick={() => onChipClick?.(chip.filterKey)}
            showDelete={!readOnly}
            sx={chip.disabled ? disabledChipSx : chipSx}
          />
        ))}

        {hiddenChips.length > 0 && (
          <Tooltip
            arrow
            placement="bottom-start"
            title={
              <Stack spacing={0.5} sx={{ py: 0.5 }}>
                {hiddenChips.map((chip) => (
                  <Typography key={chip.id} variant="caption">
                    {chip.label}
                  </Typography>
                ))}
              </Stack>
            }
          >
            <Chip
              label={`+${hiddenChips.length} more`}
              size="small"
              onClick={onOverflowClick}
              data-testid="segment-filters-more-chip"
              sx={{
                ...chipSx,
                cursor: onOverflowClick ? "pointer" : "default",
              }}
            />
          </Tooltip>
        )}

        {onClearAll && chips.length > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={onClearAll}
            sx={{ textTransform: "none", ml: 1 }}
            id="segment-filters-clear-all"
          >
            Clear All
          </Button>
        )}
      </Box>
    );
  }
);

FiltersChipsBar.displayName = "FiltersChipsBar";
